/**
 * WalkHereMenuEditor — the two knobs behind the disclosure arrow on the viewport
 * menu's "Walk here" row: the prompt the walk is generated from, and the speed
 * it walks at.
 *
 * A flyout on the row rather than a panel of its own, because that is where the
 * question is asked. You reach for these when a walk you just triggered came out
 * wrong and you want to try different prose or a different pace — so they belong
 * one gesture away from the thing that produced it, not behind a second menu
 * entry that has to be found first.
 *
 * The values live in the walkHereSettings singleton, not here: they are read by
 * runWalkHere, which is not a React component. This is only their editor, and
 * the menu mounts it — see setCommandEditor at the bottom, which is how a
 * feature contributes UI without the menu naming it.
 *
 * The fields apply LIVE and persist as you type; the button submits the WALK.
 * That split is deliberate — there is nothing to "save", and a settings form
 * with an Apply that only closes itself teaches people to look for a commit
 * step that does not exist.
 */
import { useEffect, useState } from 'react'
import { walkHereSettings, WALK_PRESETS, MIN_WALK_SPEED, MAX_WALK_SPEED } from '../lib/walkHereSettings'
import { setCommandEditor } from '../lib/viewportCommands'

export default function WalkHereMenuEditor({ run, enabled = true, reason = null }) {
  // A tick, not a copy of the state: the two derived reads below (`isDefault`,
  // `activePreset`) ask the store directly at render time, so all this has to do
  // is cause the render.
  const [, bump] = useState(0)
  // Text, not the parsed number: a half-typed "1." must stay on screen rather
  // than being rewritten to 1 under the cursor. The store gets the parsed value.
  const [speedText, setSpeedText] = useState(() => String(walkHereSettings.getState().walkSpeed))
  // The prompt needs the same treatment, and for a sharper reason: the store
  // TRIMS what it is given (so a blank prompt can never reach kimodo), and a
  // field bound straight to the stored value therefore lost every space the
  // instant it was typed — the caret sits at the end, so every space is a
  // trailing one. The field holds what was typed; the store holds it trimmed.
  const [promptText, setPromptText] = useState(() => walkHereSettings.getState().prompt)

  useEffect(() => walkHereSettings.subscribe((next) => {
    bump((n) => n + 1)
    setSpeedText(String(next.walkSpeed))
    // Only when the change came from SOMEWHERE ELSE — a preset, Reset. Echoing
    // our own trimmed value back into the field is the deletion described above.
    setPromptText((cur) => (next.prompt === cur.trim() ? cur : next.prompt))
  }), [])

  const isDefault = walkHereSettings.isDefault()
  const active = walkHereSettings.activePreset()

  return (
    <div data-testid="walk-here-settings" style={body}>
      {/* Gaits, set as a pair. The prose decides what the model produces and the
          speed decides how long the clip is for the route, so changing one
          without the other asks for a run played out over a walk's worth of
          time. Editing either field below simply deselects. */}
      <label style={label}>Gait</label>
      <div style={{ display: 'flex', gap: 4 }}>
        {WALK_PRESETS.map(p => (
          <button
            key={p.id}
            data-testid={`walk-here-preset-${p.id}`}
            onClick={() => walkHereSettings.applyPreset(p.id)}
            aria-pressed={active?.id === p.id}
            style={active?.id === p.id ? chipOn : chip}
            title={`${p.prompt} — ${p.walkSpeed} m/s`}
          >{p.label}</button>
        ))}
      </div>

      <label style={label}>Prompt</label>
      <input
        data-testid="walk-here-prompt"
        style={input}
        value={promptText}
        // The menu closes on any pointerdown outside its own subtree; this is
        // inside it, so the flyout survives being typed in. Keys are stopped so
        // Escape closes the flyout via the menu, and nothing else leaks out to
        // the viewport's own shortcuts while a field has focus.
        onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation() }}
        onChange={(e) => { setPromptText(e.target.value); walkHereSettings.set({ prompt: e.target.value }) }}
        // On the way out the field settles to exactly what the store holds:
        // trimmed, or the default when it was left blank (an empty prompt is
        // rejected by kimodo outright).
        onBlur={() => setPromptText(walkHereSettings.set({ prompt: promptText }).prompt)}
        title={`The prose sets the GAIT. The route is held by the root2d constraint, not by the wording,
so "walks" vs "strolls" vs "jogs" changes how it moves, not where.`}
      />

      <label style={label}>Speed</label>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          data-testid="walk-here-speed"
          style={{ ...input, width: 68 }}
          type="number" min={MIN_WALK_SPEED} max={MAX_WALK_SPEED} step="0.1"
          value={speedText}
          onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation() }}
          onChange={(e) => {
            setSpeedText(e.target.value)
            const n = Number(e.target.value)
            if (Number.isFinite(n) && e.target.value.trim() !== '') walkHereSettings.set({ walkSpeed: n })
          }}
          onBlur={() => setSpeedText(String(walkHereSettings.getState().walkSpeed))}
          title={`Metres per second, used to derive the clip length from the route (${MIN_WALK_SPEED}-${MAX_WALK_SPEED}).
The duration is clamped to 1.5-15 s either way, so a very low speed on a long route walks faster than asked.`}
        />
        <span style={unit}>m/s</span>
        <div style={{ flex: 1 }} />
        <button
          data-testid="walk-here-settings-reset"
          onClick={() => walkHereSettings.reset()}
          disabled={isDefault}
          style={{ ...resetBtn, opacity: isDefault ? 0.4 : 1, cursor: isDefault ? 'default' : 'pointer' }}
          title="Back to the shipped prompt and speed"
        >Reset</button>
      </div>

      {/* The point of editing these is to try one. Firing from here keeps the
          right-clicked point — reaching the row instead means closing the
          flyout, and a second right-click would land somewhere else. The fields
          themselves are already live and persisted, so this submits the WALK,
          not the settings. */}
      <button
        data-testid="walk-here-submit"
        onClick={() => run?.()}
        disabled={!enabled || !run}
        style={{ ...submitBtn, opacity: enabled && run ? 1 : 0.4, cursor: enabled && run ? 'pointer' : 'default' }}
        title={enabled ? 'Walk to the clicked point with these settings' : (reason ?? 'Unavailable here')}
      >Walk here</button>
      {!enabled && reason && <div style={reasonText}>{reason}</div>}
    </div>
  )
}

const body = { padding: '2px 2px 0', width: 232 }
const label = { display: 'block', fontSize: 9, letterSpacing: '0.06em', color: 'var(--m-fg-3)', margin: '6px 0 3px' }
const input = {
  width: '100%', boxSizing: 'border-box', padding: '4px 6px',
  background: 'var(--m-bg)', border: '1px solid var(--m-line)',
  borderRadius: 'var(--m-radius-sm)', color: 'inherit', font: 'inherit',
}
const unit = { fontFamily: 'var(--m-mono)', fontSize: 9.5, color: 'var(--m-fg-3)' }
const submitBtn = {
  display: 'block', width: '100%', marginTop: 8, padding: '5px 8px',
  background: 'var(--m-accent)', color: 'var(--m-accent-ink)',
  border: '1px solid var(--m-accent)', borderRadius: 'var(--m-radius-sm)',
  font: 'inherit', fontWeight: 600,
}
const reasonText = {
  marginTop: 4, fontFamily: 'var(--m-mono)', fontSize: 10, lineHeight: 1.3,
  color: 'var(--m-warn)',
}
const chip = {
  flex: 1, padding: '3px 6px', background: 'none',
  border: '1px solid var(--m-line)', borderRadius: 'var(--m-radius-sm)',
  color: 'inherit', font: 'inherit', cursor: 'pointer',
}
const chipOn = {
  ...chip,
  background: 'var(--m-accent-soft)', borderColor: 'var(--m-accent)', color: 'var(--m-fg)',
}
const resetBtn = {
  background: 'none', border: '1px solid var(--m-line)', borderRadius: 'var(--m-radius-sm)',
  color: 'inherit', font: 'inherit', padding: '3px 8px',
}

// The feature contributing its own UI. Registered here rather than in
// walkHereCommand.js so that lib module stays free of React, and rather than in
// ViewportMenu so the menu never names a specific command.
setCommandEditor('walkHere', WalkHereMenuEditor)
